import {dashboardReducer, completeLayout, minRows, inclusiveRange, inclusiveEnd, isReport, viewerParams, normalParams, paramsEqual, saveOperations, validReport, periodLabel, compareLabel, yearOf} from './data-dashboards.mjs';
import {QueryResult, downloadCsv} from './chat-results.jsx';
import {validResult} from './chat-results.mjs';
import {ChatChart} from './chat-render.jsx';
import {PanelPresentation} from './data-dashboard-presentation.jsx';
import {Modal} from './accessibility.jsx';
import {confirmAction, promptAction} from './feedback.jsx';
import {Icon, ReportAppBar, ReportTabs, FilterPane, VisualTools, ReportVisual, ReportState, ReportChips} from './bi-report.jsx';
import {ReportBuilder} from './bi-builder.jsx';
import {visualSubtitle, fieldTitle, dateFieldOf, panelError, withChoice, chipsOf, csvOf, tableOf, stableOrder} from './bi-report.mjs';

const {useState,useEffect,useRef,useCallback,useId} = React;
const localDay = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
const VALUES_SHOWN = 60;
const renderChart = (spec,lang,height) => <ChatChart spec={spec} lang={lang} height={height} showValues={false}/>;
const message = (he,conflict=false,retry=null,action=null) => conflict
  ? he ? 'הלוח השתנה. הפרטים נטענו מחדש; החילו את השינוי שוב.' : 'This dashboard changed. Details reloaded; apply your change again.'
  : retry ? he ? `יותר מדי בקשות. נסו שוב בעוד ${retry} שניות.` : `Too many requests. Try again in ${retry} seconds.`
    : action==='save' ? he ? 'לא ניתן לשמור את השינוי. נסו שוב.' : 'Could not save the change. Try again.'
    : action==='delete' ? he ? 'לא ניתן למחוק את הלוח. נסו שוב.' : 'Could not delete the dashboard. Try again.'
    : he ? 'לא ניתן לטעון את הלוח. נסו לרענן או לפתוח אותו שוב.' : 'Could not load the dashboard. Refresh or open it again.';

function packed(panels) {
  let x=0, y=0, height=0;
  return panels.map(panel => {
    const {w,h} = panel.layout;
    if (x+w>12) {x=0;y+=height;height=0;}
    const next = {...panel,layout:{x,y,w,h}};
    x+=w; height=Math.max(height,h);
    return next;
  });
}

class GridBoundary extends React.Component {
  state = {failed:false};
  static getDerivedStateFromError() { return {failed:true}; }
  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

export function SaveToDashboard({artifact,lang,env,scopeKey,onSaved}) {
  const he = lang==='he';
  const [boards,setBoards] = useState([]);
  const [selected,setSelected] = useState('');
  const [title,setTitle] = useState('');
  const [busy,setBusy] = useState(true);
  const [error,setError] = useState('');
  const alive = useRef(true);
  useEffect(() => {
    alive.current=true;
    PCMAdapter.listDataDashboards(env).then(list=>{if(alive.current){setBoards(Array.isArray(list)?list:[]);setBusy(false);}})
      .catch(()=>{if(alive.current){setError(message(he));setBusy(false);}});
    return ()=>{alive.current=false;};
  },[scopeKey,env]);
  const save = async event => {
    event.preventDefault();
    if (busy || !artifact.source_id) return;
    setBusy(true);setError('');
    try {
      let board;
      if (selected) board = await PCMAdapter.getDataDashboard(selected,env);
      else {
        board = (await PCMAdapter.createDataDashboard(env,title.trim())).dashboard;
        if (alive.current) {
          setBoards(list=>[...list,{id:board.id,title:board.title,revision:board.revision}]);
          setSelected(board.id);
        }
      }
      if (!alive.current) return;
      if (board.panels.length>=12) {setError(he?'הלוח מלא (12 פריטים). בחרו לוח אחר.':'This dashboard is full (12 panels). Choose another.');return;}
      const presentation={title:artifact.title,view:artifact.view,...(artifact.chart_type?{chart_type:artifact.chart_type}:{}),...(artifact.view==='chart'?{x:artifact.x,y:artifact.y}:artifact.view==='tree'?{parent:artifact.parent,child:artifact.child}:{})};
      const reply = await PCMAdapter.patchDataDashboard(board.id,env,{expected_revision:board.revision,operations:[{op:'add_panel',source_id:artifact.source_id,presentation}]});
      if (alive.current) onSaved(reply.dashboard);
    } catch (e) {
      if (!alive.current) return;
      if (e.status===409 && selected) {
        try { await PCMAdapter.getDataDashboard(selected,env); } catch { /* The next explicit save rechecks access. */ }
      }
      if (alive.current) setError(message(he,e.status===409,e.retryAfter));
    } finally { if (alive.current) setBusy(false); }
  };
  return <Modal label={he?'שמירה ללוח נתונים':'Save to dashboard'} onClose={()=>onSaved(null)} dir={he?'rtl':'ltr'}>
    <form className="data-dashboard-save" onSubmit={save}>
      <h2>{he?'שמירה ללוח נתונים':'Save to dashboard'}</h2>
      <p className="muted">{he?'נשמרת הגדרת השאילתה. בפתיחת הלוח הנתונים נקראים מחדש.':'Saves the query definition. Opening the dashboard reads current data.'}</p>
      <label>{he?'לוח נתונים':'Dashboard'}<select className="input" value={selected} onChange={e=>setSelected(e.target.value)} disabled={busy}>
        <option value="">{he?'יצירת לוח חדש':'Create a new dashboard'}</option>
        {boards.map(board=><option key={board.id} value={board.id}>{board.title}</option>)}
      </select></label>
      {!selected && <label>{he?'שם הלוח':'Dashboard title'}<input className="input" value={title} required maxLength={160} onChange={e=>setTitle(e.target.value)} disabled={busy}/></label>}
      {error && <p className="set-error" role="alert">{error}</p>}
      <div className="dlg-actions"><button className="btn" type="button" onClick={()=>onSaved(null)}>{he?'ביטול':'Cancel'}</button>
        <button className="btn primary" disabled={busy||(!selected&&!title.trim())} type="submit">{he?'שמירה':'Save'}</button></div>
    </form>
  </Modal>;
}

// report: null on a plain dashboard; on a report board the viewer's params and the frame's tools (bi-report.jsx).
function Panel({panel,result,pending,error,index,count,lang,busy,mutate,arrange,refresh,report=null}) {
  const he=lang==='he';
  const savedPresentation=JSON.stringify([panel.source_id,panel.presentation]);
  const [preview,setPreview]=useState(null);
  useEffect(()=>setPreview(null),[savedPresentation]);
  const [heightDraft,setHeightDraft]=useState(String(panel.layout.h));
  useEffect(()=>{if(!busy)setHeightDraft(String(panel.layout.h));},[panel.layout.h,busy]);
  const commitHeight=value=>{
    const height=Number(value);
    if(Number.isInteger(height)&&height>=minRows(panel)&&height<=12) {
      if(height!==panel.layout.h)arrange(panel.id,0,null,height);
    }else setHeightDraft(String(panel.layout.h));
  };
  const title=panel.presentation?.title || (he?'פריט לא זמין':'Unavailable panel');
  const edit = async () => {
    const value=await promptAction(he?'שם הפריט':'Panel title',title,{lang});
    if(!value?.trim()) return;
    mutate([panel.definition ? {op:'edit_definition',panel_id:panel.id,definition:{...panel.definition,title:value.trim()}} : {op:'edit_panel',panel_id:panel.id,presentation:{title:value.trim()}}]);
  };
  // download where a table is on screen, as the comp's pivot card: a pivot, a table, or a chart in table view
  const tabular=!!panel.definition&&!!report&&(['pivot','table'].includes(panel.definition.kind)||report.table&&panel.definition.kind!=='kpi');
  const subtitle=panel.definition&&report&&visualSubtitle({definition:panel.definition,title,result:result?.report?.result,params:report.params,dataset:report.dataset,today:report.today,lang});
  const refreshButton=<button className="btn btn-sm" type="button" disabled={busy||!panel.available} onClick={()=>refresh(panel.id)}>{he?'רענון פריט':'Refresh panel'}</button>;
  const options=<details className="data-dashboard-panel-options"><summary>{he?'אפשרויות פריט':'Panel controls'}</summary>
      <div className="data-dashboard-panel-tools">
        {report&&refreshButton}
        <button type="button" className="btn btn-sm" disabled={busy||index===0} onClick={()=>arrange(panel.id,-1)}>{he?'הזזה למעלה':'Move up'}</button>
        <button type="button" className="btn btn-sm" disabled={busy||index===count-1} onClick={()=>arrange(panel.id,1)}>{he?'הזזה למטה':'Move down'}</button>
        <label><span className="sr-only">{he?'רוחב':'Width'} {title}</span><select className="input" value={panel.layout.w} disabled={busy} onChange={e=>arrange(panel.id,0,Number(e.target.value))}>
          {![4,6,12].includes(panel.layout.w)&&<option value={panel.layout.w}>{panel.layout.w}/12</option>}
          <option value={4}>{he?'קטן':'Small'}</option><option value={6}>{he?'בינוני':'Medium'}</option><option value={12}>{he?'מלא':'Full'}</option>
        </select></label>
        <label><span>{he?'גובה':'Height'} <span className="sr-only">{title}</span></span><input className="input" type="number" min={minRows(panel)} max={12} step={1} value={heightDraft} disabled={busy}
          onChange={e=>setHeightDraft(e.target.value)} onBlur={e=>commitHeight(e.target.value)} onKeyDown={e=>{
            if(e.key==='Enter'){e.preventDefault();e.currentTarget.blur();}
            if(e.key==='Escape'){e.currentTarget.value=String(panel.layout.h);setHeightDraft(String(panel.layout.h));e.currentTarget.blur();}
            if(['ArrowUp','ArrowDown'].includes(e.key)){e.preventDefault();const height=Math.max(minRows(panel),Math.min(12,(Number(heightDraft)||panel.layout.h)+(e.key==='ArrowUp'?1:-1)));setHeightDraft(String(height));commitHeight(height);}
          }}/></label>
        <button type="button" className="btn btn-sm" disabled={busy||!panel.available} onClick={edit}>{he?'שינוי שם':'Rename panel'}</button>
        <button type="button" className="btn btn-sm" disabled={busy} onClick={()=>mutate([{op:'remove_panel',panel_id:panel.id}])}>{he?'הסרה':'Remove panel'}</button>
      </div></details>;
  return <article className="data-dashboard-panel" data-panel-id={panel.id} data-kind={panel.definition?.kind} dir={he?'rtl':'ltr'}>
    <div className="data-dashboard-panel-head">
      <button type="button" className="btn btn-sm data-dashboard-drag" aria-label={`${he?'גרירת':'Drag'} ${title}`} disabled={busy}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" aria-hidden="true"><path d="M5 3h1M10 3h1M5 8h1M10 8h1M5 13h1M10 13h1" strokeWidth="2" strokeLinecap="round"/></svg>
      </button>
      {report?<div className="bi-visual-title"><h3 dir="auto">{title}</h3>{subtitle?.length>0&&<p className="bi-visual-sub" title={subtitle.join(' · ')}>{subtitle.map((part,i)=><React.Fragment key={i}>{i>0&&' · '}<bdi>{part}</bdi></React.Fragment>)}</p>}</div>:<h3 dir="auto">{title}</h3>}
      {report?<VisualTools title={title} table={report.table} onTable={panel.definition?report.onTable:null} focused={report.focused} onFocus={report.onFocus}
        onCsv={!tabular?undefined:result?.report&&tableOf(panel.definition,result.report.result,report.dataset,lang).rows.length?()=>downloadCsv(title,csvOf(panel.definition,result.report.result,report.dataset,lang)):null} lang={lang}/>:refreshButton}
    </div>
    {!report&&options}
    {panel.available&&!panel.definition&&<PanelPresentation key={savedPresentation} panel={panel} artifact={result?.artifact} lang={lang} busy={busy} onPreview={setPreview} onSave={presentation=>mutate([{op:'edit_panel',panel_id:panel.id,presentation}])}/>}
    {panel.definition ? <>
      {pending&&<p className="data-dashboard-status" role="status">{he?'קורא נתונים עדכניים…':'Reading current data…'}</p>}
      {error&&<div className="bi-visual-body"><ReportState {...(typeof error==='string'?{state:'failed',text:error}:error)} lang={lang}/></div>}
      {result?.report ? <div className="bi-visual-body"><ReportVisual definition={panel.definition} report={result.report} title={title} dataset={report?.dataset}
        params={report?.params} today={report?.today} table={report?.table} height={report?.height} onPick={report?.onPick} onOrder={report?.onOrder} lang={lang}/></div>
        : !pending && !error && <p className="data-dashboard-status">{he?'בחרו רענון לקריאת הנתונים.':'Refresh to read current data.'}</p>}
    </> : !panel.available ? <p className="query-result-limit">{he?'אין גישה למקור הפריט.':'This panel’s source is unavailable.'}</p> : <>
      {pending&&<p className="data-dashboard-status" role="status">{he?'קורא נתונים עדכניים…':'Reading current data…'}</p>}
      {error&&<p className="query-result-limit" role="alert">{error}</p>}
      {result ? <QueryResult artifact={{...result.artifact,...panel.presentation,...preview}} lang={lang} expanded compact renderChart={renderChart} sourceNote={panel.source.parameter_mode==='date_range_v1'
        ? he?'עוקב אחר תאריכי הלוח.':'Follows dashboard dates.'
        : he?'טווח קבוע · אינו עוקב אחר בחירת התאריכים.':'Fixed range · does not follow the date selector.'}/>
        : !pending && !error && <p className="data-dashboard-status">{he?'בחרו רענון לקריאת הנתונים.':'Refresh to read current data.'}</p>}
      {!result&&<details className="query-result-query"><summary>{he?'שאילתת המקור':'Source query'}</summary><pre className="chat-code mono" dir="ltr">{panel.source.query}</pre></details>}
    </>}
    {report&&options}
  </article>;
}

export function DataDashboards({lang,env,scopeKey,onSelectDashboard,selectedDashboardId=null,refreshVersion=0,visible=true}) {
  const he=lang==='he';
  const [state,setState]=useState(()=>dashboardReducer(undefined,{type:'scope',scopeKey}));
  const current=useRef(state);
  const [boards,setBoards]=useState([]);
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState('');
  const [start,setStart]=useState('');
  const [end,setEnd]=useState('');
  const [gridReady,setGridReady]=useState(false);
  // the report page: the pane starts collapsed on a phone; the catalog and a choice's values are read once per page
  const [paneOpen,setPaneOpen]=useState(()=>!window.matchMedia?.('(max-width: 900px)').matches);
  const [focusId,setFocusId]=useState(null);
  const [tableView,setTableView]=useState({});
  const [readAt,setReadAt]=useState(null);
  const [datasets,setDatasets]=useState({});
  const [choiceValues,setChoiceValues]=useState({});
  const fetched=useRef(new Set());
  const orders=useRef({});  // each panel's entity order, by its dimensions: a colour follows its entity
  const restoreFocus=useRef(null);
  // the builder tab (Task 5.1): it mounts on first open and stays mounted, hidden, so its wells survive a tab switch
  const [tab,setTab]=useState('board');
  const [built,setBuilt]=useState(false);
  const [reveal,setReveal]=useState(null);
  const refocus=useRef(false);  // leaving the builder on a plain board unmounts the tabs under the focus: hand it on after the render
  const openTab=next=>{if(next==='builder')setBuilt(true);else if(tab==='builder')refocus.current=true;setTab(next);};
  const uid=useId();
  const root=useRef(null);
  const request=useRef({generation:0,controller:null});
  const scope=useRef(scopeKey);scope.current=scopeKey;
  const update=event=>{current.current=dashboardReducer(current.current,event);setState(current.current);return current.current;};
  const cancel=()=>{request.current.controller?.abort();request.current={generation:request.current.generation+1,controller:new AbortController()};return request.current;};
  const active=job=>request.current===job&&!job.controller.signal.aborted&&scope.current===scopeKey;
  const list=useCallback(async()=>{
    const key=scopeKey;
    try { const reply=await PCMAdapter.listDataDashboards(env);if(scope.current===key)setBoards(Array.isArray(reply)?reply:[]); }
    catch {if(scope.current===key){cancel();update({type:'clear'});setBoards([]);setBusy(false);setError(message(he));}}
  },[env,scopeKey,he]);
  const refresh=async(definition,ids,job)=>{
    for(const panel of definition.panels) {
      if(!active(job)) return;
      if(!panel.available||!ids.includes(panel.id)) continue;
      const next=update({type:'read',panelId:panel.id});
      const token=next.pending[panel.id];
      try {
        const reply=await PCMAdapter.refreshDataDashboardPanel(definition.id,panel.id,env,definition.revision,{signal:job.controller.signal});
        if(!active(job))return;
        if(reply.dashboard_id!==definition.id||reply.panel_id!==panel.id||reply.revision!==definition.revision) throw new Error('Stale dashboard result');
        if(panel.definition) {
          if(!validReport(reply)) throw new Error('Stale dashboard result');
          update({type:'result',panelId:panel.id,token,report:{result:reply.result,prescription:reply.prescription}});
        } else {
          // a saved preset ("this year") resolves in the server's today, which the page cannot recompute
          const relative=definition.params?.period&&definition.params.period.preset!=='custom';
          const expected=panel.source.parameter_mode==='date_range_v1' ? definition.range||panel.source.default_range : null;
          if(reply.source_id!==panel.source_id||(!relative&&JSON.stringify(reply.range)!==JSON.stringify(expected))||!validResult(reply.artifact)||reply.artifact.company!==env||reply.artifact.source_id!==panel.source_id) throw new Error('Stale dashboard result');
          update({type:'result',panelId:panel.id,token,artifact:reply.artifact});
        }
        setReadAt(new Date());
      } catch(e) {
        if(!active(job))return;
        // a definition panel's own refusal is that panel's state; a missing board still clears the page
        const state=panel.definition?panelError(e,lang):null;
        if([401,403].includes(e.status)||e.status===404&&!(state&&e.detail==='panel unavailable')) {update({type:'clear'});setError(message(he));return;}
        if(e.status===409) {await load(definition.id,true);return;}
        update({type:'failure',panelId:panel.id,token,error:state||message(he,false,e.retryAfter)});
      }
    }
  };
  // One refresh-board for the viewer's unsaved params (spec §5.2): every panel, one request, nothing written.
  const boardEntry=(definition,entry)=>{
    const panel=definition.panels.find(p=>p.id===entry?.panel_id);
    if(!panel)return {panelId:null};
    if(entry.error&&panel.definition)return {panelId:panel.id,error:panelError(entry.error,lang)||message(he)};
    if(entry.error)return {panelId:panel.id,error:entry.available===false?(he?'אין גישה למקור הפריט.':'This panel’s source is unavailable.'):message(he,false,null)};
    if(entry.dashboard_id!==definition.id||entry.revision!==definition.revision)return {panelId:panel.id,error:message(he)};
    if(panel.definition)return validReport(entry)?{panelId:panel.id,report:{result:entry.result,prescription:entry.prescription}}:{panelId:panel.id,error:message(he)};
    return entry.source_id===panel.source_id&&validResult(entry.artifact)&&entry.artifact.company===env&&entry.artifact.source_id===panel.source_id
      ?{panelId:panel.id,artifact:entry.artifact}:{panelId:panel.id,error:message(he)};
  };
  const refreshBoard=async(definition,job)=>{
    const next=update({type:'boardRead'});
    const token=next.board;
    try {
      const reply=await PCMAdapter.refreshDataDashboardBoard(definition.id,env,definition.revision,viewerParams(next),{signal:job.controller.signal});
      if(!active(job))return;
      const entries=(Array.isArray(reply?.panels)?reply.panels:[]).map(entry=>boardEntry(definition,entry));
      // a reply for another revision or board is dropped by the reducer; the panels then say so instead of waiting
      if(update({type:'boardResult',token,reply,entries,error:message(he)}).board===token)update({type:'boardFailure',token,error:message(he)});
      else setReadAt(new Date());
    } catch(e) {
      if(!active(job))return;
      if([401,403,404].includes(e.status)) {update({type:'clear'});setError(message(he));return;}
      if(e.status===409) {await load(definition.id,true);return;}
      update({type:'boardFailure',token,error:message(he,false,e.retryAfter)});
    }
  };
  const refreshAll=(definition,ids,job)=>!ids.length?null:current.current.draft?refreshBoard(definition,job):refresh(definition,ids,job);
  const load=async(id,conflict=false,notice='')=>{
    const job=cancel();setBusy(true);setError('');
    if(!id){update({type:'clear'});setBusy(false);return;}
    try {
      const definition=await PCMAdapter.getDataDashboard(id,env);
      if(!active(job))return;
      const next=update({type:'metadata',definition});
      setStart(definition.range?.start||'');setEnd(inclusiveEnd(definition.range));
      if(conflict||notice)setError(conflict?message(he,true):notice);
      await refreshAll(definition,definition.panels.filter(p=>!next.results[p.id]&&!next.errors[p.id]).map(p=>p.id),job);
    } catch(e) {if(active(job)){update({type:'clear'});setError(message(he,false,e.retryAfter));}}
    finally {if(active(job))setBusy(false);}
  };
  const recover=async(definition,error,action)=>{
    update({type:'clear'});
    if([401,403,404].includes(error.status))setError(message(he));
    else await load(definition.id,error.status===409,message(he,false,error.retryAfter,action));
  };
  useEffect(()=>{
    try {setGridReady(!!window.PCMDashboardGrid && !!document.querySelector('link[data-dashboard-grid-css]')?.sheet?.cssRules.length && getComputedStyle(root.current).getPropertyValue('--data-dashboard-css').trim()==='ready');}
    catch {setGridReady(false);}
    return ()=>{cancel();scope.current=null;};
  },[scopeKey,env]);
  useEffect(()=>{if(visible)list();},[visible,scopeKey,env]);
  useEffect(()=>{
    if(visible)load(selectedDashboardId);
    else{cancel();update({type:'clear'});setBusy(false);setError('');}
  },[selectedDashboardId,refreshVersion,visible]);
  const mutate=async operations=>{
    const definition=current.current.definition;
    if(busy||!definition||scope.current!==scopeKey)return;
    const changed=definition.panels.filter(panel=>operations.some(op=>op.op==='set_range'&&panel.source?.parameter_mode==='date_range_v1'||['remove_panel','replace_source'].includes(op.op)&&op.panel_id===panel.id)).map(p=>p.id);
    const job=cancel();update({type:'invalidate',panelIds:changed});setBusy(true);setError('');
    try {
      const reply=await PCMAdapter.patchDataDashboard(definition.id,env,{expected_revision:definition.revision,operations});
      if(!active(job))return;
      const next=update({type:'metadata',definition:reply.dashboard});
      setStart(reply.dashboard.range?.start||'');setEnd(inclusiveEnd(reply.dashboard.range));
      await refreshAll(reply.dashboard,reply.dashboard.panels.filter(p=>!next.results[p.id]&&!next.errors[p.id]).map(p=>p.id),job);
    // a conflict on Save reloads the board without clearing it, so the viewer's unsaved filters survive to be saved again
    }catch(e){if(active(job))await (e.status===409&&operations.some(op=>op.op==='set_params')?load(definition.id,true):recover(definition,e,'save'));}
    finally{if(active(job))setBusy(false);}
  };
  const arrange=(id,delta,width,height)=>{
    const panels=current.current.definition.panels;
    const index=panels.findIndex(p=>p.id===id);
    let ordered=panels.map(p=>p.id===id&&(width||height)?{...p,layout:{...p.layout,...(width?{w:width}:{}),...(height?{h:height}:{})}}:p);
    if(delta) {ordered=ordered.filter(p=>p.id!==id);ordered.splice(index+delta,0,panels[index]);}
    mutate([{op:'set_layout',layout:completeLayout(packed(ordered))}]);
  };
  const refreshOne=async id=>{
    if(busy)return;
    const definition=current.current.definition;
    const job=cancel();setBusy(true);setError('');
    await refreshAll(definition,id?[id]:definition.panels.map(p=>p.id),job);
    if(active(job))setBusy(false);
  };
  // A filter change: the viewer's params change for this page only, read with one refresh-board (no PATCH).
  const changeParams=params=>{
    const definition=current.current.definition;
    if(busy||!definition||scope.current!==scopeKey||paramsEqual(params,viewerParams(current.current)))return;
    update({type:'params',params});
    refreshBoard(current.current.definition,cancel());
  };
  // The builder's Save (spec §7.4): one add_definition_panel on the open board, or, with none open, on the board
  // picked for it or a new one named after the panel; then the board tab shows the new panel. The builder shows
  // every refusal itself (a 422 by its well), so nothing here clears the board.
  const saveReport=async(panel,target)=>{
    const open=current.current.definition;
    const job=cancel();setBusy(true);setError('');
    let made=null;
    try {
      let board=open;
      if(!board&&target)board=await PCMAdapter.getDataDashboard(target,env);
      if(!board)board=made=(await PCMAdapter.createDataDashboard(env,panel.title.slice(0,160))).dashboard;
      const reply=await PCMAdapter.patchDataDashboard(board.id,env,{expected_revision:board.revision,operations:[{op:'add_definition_panel',definition:panel}]});
      if(scope.current!==scopeKey)return;
      setReveal(reply.created_panels?.[0]?.panel_id??null);setTab('board');
      if(open&&active(job)) {
        const next=update({type:'metadata',definition:reply.dashboard});
        await refreshAll(reply.dashboard,reply.dashboard.panels.filter(p=>!next.results[p.id]&&!next.errors[p.id]).map(p=>p.id),job);
      } else {await list();onSelectDashboard(reply.dashboard.id);}
    } catch(e) {
      // a conflict reloads the open board for the next Save; a board made for this Save stays, opened, so a retry uses it
      if(e.status===409&&open&&active(job))await load(open.id);
      else if(made&&scope.current===scopeKey){await list();onSelectDashboard(made.id);}
      throw e;
    } finally {if(active(job))setBusy(false);}
  };
  useEffect(()=>{
    if(!reveal||tab!=='board')return;
    const panel=root.current?.querySelector(`[data-panel-id="${CSS.escape(reveal)}"]`);
    if(!panel)return;  // the board is still loading: this runs again when it renders
    setReveal(null);
    panel.scrollIntoView({block:'nearest'});
    panel.querySelector('[data-focus-tool]')?.focus({preventScroll:true});
  },[reveal,tab,state.definition]);
  const toggleFocus=id=>{if(focusId===id){restoreFocus.current=id;setFocusId(null);}else setFocusId(id);};
  const create=async()=>{
    const title=await promptAction(he?'שם הלוח החדש':'New dashboard title','',{lang});
    if(!title?.trim()||scope.current!==scopeKey)return;
    setBusy(true);
    try{const reply=await PCMAdapter.createDataDashboard(env,title.trim());if(scope.current===scopeKey){await list();onSelectDashboard(reply.dashboard.id);}}
    catch(e){if(scope.current===scopeKey)setError(message(he,false,e.retryAfter));}
    finally{if(scope.current===scopeKey)setBusy(false);}
  };
  const remove=async()=>{
    const definition=current.current.definition;
    if(!definition||!await confirmAction(he?'למחוק את הלוח ואת הפריטים שלו?':'Delete this dashboard and its panels?',{lang,danger:true,confirmLabel:he?'מחיקה':'Delete'}))return;
    if(scope.current!==scopeKey)return;
    const job=cancel();update({type:'clear'});setBusy(true);
    try{await PCMAdapter.deleteDataDashboard(definition.id,env,definition.revision);if(active(job)){onSelectDashboard(null);await list();}}
    catch(e){if(active(job))await recover(definition,e,'delete');}
    finally{if(active(job))setBusy(false);}
  };
  const definition=state.definition;
  const report=isReport(definition);
  const params=viewerParams(state);
  const today=localDay();
  const choiceKeys=JSON.stringify(Object.values(params.choices).map(c=>[c.dataset,c.field]));
  useEffect(()=>{
    if(!report)return;
    const keys=new Set([...definition.panels.filter(p=>p.definition).map(p=>p.definition.dataset),...Object.values(params.choices).map(c=>c.dataset)]);
    for(const key of keys) {
      if(fetched.current.has(`dataset:${key}`))continue;
      fetched.current.add(`dataset:${key}`);
      PCMAdapter.getBiDataset(key,env).then(ds=>{if(scope.current===scopeKey)setDatasets(m=>({...m,[key]:ds}));}).catch(()=>{fetched.current.delete(`dataset:${key}`);/* titles fall back to field names; the next revision asks again */});
    }
  },[report,definition?.id,definition?.revision,choiceKeys]);
  useEffect(()=>{
    if(!report||!paneOpen)return;
    for(const c of Object.values(params.choices)) {
      const key=`values:${c.dataset}:${c.field}`;
      if(fetched.current.has(key))continue;
      fetched.current.add(key);
      setChoiceValues(m=>({...m,[key]:{state:'loading',values:[]}}));
      // the dimension's values in the period on show, most rows first (spec Task 2.5's preview, one table panel)
      PCMAdapter.previewBiPanel(env,{kind:'table',dataset:c.dataset,rows:[c.field],values:['__count']},{period:params.period,choices:{},compare:false})
        .then(reply=>{if(scope.current===scopeKey)setChoiceValues(m=>({...m,[key]:{state:'ready',values:[...new Set((reply?.result?.rows||[]).map(r=>r[0]).filter(v=>v!==null&&v!==undefined&&v!=='').map(String))].slice(0,VALUES_SHOWN)}}));})
        .catch(()=>{fetched.current.delete(key);if(scope.current===scopeKey)setChoiceValues(m=>({...m,[key]:{state:'error',values:[]}}));});  // reopening the pane asks again
    }
  },[report,paneOpen,choiceKeys]);
  useEffect(()=>{
    if(focusId) {root.current?.querySelector('.bi-focus [data-focus-tool]')?.focus();return undefined;}
    if(restoreFocus.current) {
      const id=restoreFocus.current;restoreFocus.current=null;
      root.current?.querySelector(`[data-panel-id="${CSS.escape(id)}"] [data-focus-tool]`)?.focus();
    }
    return undefined;
  },[focusId]);
  useEffect(()=>{
    if(!focusId)return undefined;
    const leave=event=>{if(event.key==='Escape'&&!event.defaultPrevented&&!document.querySelector('[role="dialog"]')){event.preventDefault();toggleFocus(focusId);}};
    document.addEventListener('keydown',leave);
    return ()=>document.removeEventListener('keydown',leave);
  },[focusId]);
  const supported=definition?.panels.filter(p=>p.available&&p.source?.parameter_mode==='date_range_v1').length||0;
  const orderOf=(id,keys)=>{const next=stableOrder(orders.current[id],keys);if(next!==orders.current[id])orders.current={...orders.current,[id]:next};return next;};
  const frame=(panel,inFocus)=>({params,today,dataset:datasets[panel.definition?.dataset],table:!!tableView[panel.id],onPick:(name,value)=>changeParams(withChoice(params,name,value)),
    onOrder:(dim,keys)=>orderOf(`${panel.id}:${dim}`,keys),
    onTable:()=>setTableView(t=>({...t,[panel.id]:!t[panel.id]})),focused:inFocus,onFocus:()=>toggleFocus(panel.id),
    height:inFocus?440:Math.max(minRows(panel)*90,panel.layout.h*140-170)});
  const cell=(panel,index,inFocus=false)=><Panel panel={panel} result={state.results[panel.id]} pending={!!state.pending[panel.id]} error={state.errors[panel.id]} index={index} count={definition.panels.length} lang={lang} busy={busy} mutate={mutate} arrange={arrange} refresh={refreshOne} report={report?frame(panel,inFocus):null}/>;
  const cards=definition?.panels.map((panel,index)=><div key={panel.id} className="data-dashboard-cell">{cell(panel,index)}</div>);
  const focusAt=report&&focusId?definition.panels.findIndex(p=>p.id===focusId):-1;
  const fallback=<><p className="set-error" role="status">{he?'סידור בגרירה אינו זמין. השתמשו בכפתורי הסידור.':'Drag layout is unavailable. Use the arrangement buttons.'}</p><div className="data-dashboard-fallback">{cards}</div></>;
  const Grid=window.PCMDashboardGrid?.Grid;
  const years=[...new Set([...[2,1,0].map(n=>Number(today.slice(0,4))-n),yearOf(params.period,today)].filter(Boolean))].sort();
  const dateTitles=report?[...new Set(definition.panels.filter(p=>p.definition).map(p=>{const ds=datasets[p.definition.dataset],f=dateFieldOf(p.definition,ds);return f&&fieldTitle(ds,f,lang);}).filter(Boolean))]:[];
  const choices=Object.entries(params.choices).map(([name,c])=>{
    const loaded=choiceValues[`values:${c.dataset}:${c.field}`];
    return {name,title:fieldTitle(datasets[c.dataset],c.field,lang),state:loaded?.state,values:[...new Set([...(c.value!==null?[c.value]:[]),...(loaded?.values||[])])]};
  });
  const grid=!definition?.panels.length?<p className="data-dashboard-empty">{he?'שאלו שאלה בשיחה ושמרו את התוצאה ללוח הזה.':'Ask a question in Talk and save its result to this dashboard.'}</p>
    : focusAt>=0?<div className="bi-focus">{cell(definition.panels[focusAt],focusAt,true)}</div>
    : gridReady?<GridBoundary key={definition.id} fallback={fallback}><Grid layout={definition.panels.map(p=>({i:p.id,...p.layout}))} disabled={busy} dragHandle=".data-dashboard-drag" rowHeight={140} onLayoutChange={layout=>{
      if(busy)return;
      const geometry=new Map(layout.map(p=>[p.i,{x:p.x,y:p.y,w:p.w,h:p.h}]));
      const ordered=[...definition.panels].sort((a,b)=>geometry.get(a.id).y-geometry.get(b.id).y||geometry.get(a.id).x-geometry.get(b.id).x);
      const next=ordered.map(p=>({...p,layout:geometry.get(p.id)}));
      try{const full=completeLayout(next);if(JSON.stringify(full)!==JSON.stringify(completeLayout(definition.panels)))mutate([{op:'set_layout',layout:full}]);}catch{setError(he?'לא ניתן לשמור את הסידור. נסו בכפתורי הסידור.':'Could not save the layout. Use the arrangement buttons.');}
    }}>{cards}</Grid></GridBoundary>:fallback;
  // the report chrome shows on a report board, and around the builder whatever board is open (or none)
  const tabs=report||tab==='builder';
  const building=tabs&&tab==='builder';
  useEffect(()=>{
    if(!refocus.current||building)return;
    refocus.current=false;
    if(!tabs)(root.current?.querySelector('.data-dashboard-manage > summary')||root.current?.querySelector('.data-dashboard-heading')||root.current?.querySelector('[data-open-builder]'))?.focus();
  },[tab,tabs,building]);
  const builderWord=<><Icon name="pivot"/>{he?'בונה דוחות':'Report builder'}</>;
  return <section className={tabs?'data-dashboards bi-report':'data-dashboards'} ref={root} dir={he?'rtl':'ltr'} aria-label={he?'לוחות נתונים':'Dashboards'}>
    {tabs&&<ReportAppBar title={definition?.title||(he?'בונה דוחות':'Report builder')} env={env} readAt={report?readAt:null} lang={lang}/>}
    {tabs&&<ReportTabs id={uid} tab={tab} onTab={openTab} lang={lang}/>}
    <div className="data-dashboard-toolbar data-dashboard-header">
      {definition&&!building&&<div className="data-dashboard-context"><h2 className="data-dashboard-heading" dir="auto">{definition.title}</h2>
        {report?<ReportChips period={periodLabel(params.period,today,lang)} compare={params.compare&&params.period?compareLabel(params.period,today,lang):null}
          chips={chipsOf(params,datasets,lang)} unsaved={!!state.draft} disabled={busy} onRemove={name=>changeParams(withChoice(params,name,null))} lang={lang}/>
        :<p>{definition.range?<>{he?'תאריכי הלוח':'Dashboard dates'} · {definition.range.start} – {inclusiveEnd(definition.range)}</>
          :he?'לפי טווחי התאריכים המקוריים של הפריטים':'Using each panel’s original date range'}</p>}
      </div>}
      <label><span>{he?'לוח נתונים':'Dashboard'}</span><select className="input" value={selectedDashboardId||''} disabled={busy} onChange={e=>onSelectDashboard(e.target.value||null)}>
        <option value="">{he?'בחירת לוח':'Choose a dashboard'}</option>
        {boards.map(board=><option key={board.id} value={board.id}>{board.id===definition?.id?definition.title:board.title}</option>)}
        {definition&&!boards.some(b=>b.id===definition.id)&&<option value={definition.id}>{definition.title}</option>}
      </select></label>
      {definition&&!building&&<button type="button" className="btn primary" disabled={busy} onClick={()=>refreshOne(null)}>{he?'רענון הלוח':'Refresh dashboard'}</button>}
    </div>
    {error&&<p className="set-error" role="alert">{error}</p>}
    {busy&&<p className="data-dashboard-status" role="status">{he?'טוען לוח נתונים…':'Loading dashboard…'}</p>}
    {building?<div role="tabpanel" id={`${uid}-panel`} aria-labelledby={`${uid}-tab`} hidden/>
    :definition?<>
      <div className="data-dashboard-options">
      <details className="data-dashboard-manage"><summary>{he?'ניהול הלוח':'Manage dashboard'}</summary><div className="data-dashboard-toolbar">
        <button type="button" className="btn" disabled={busy} onClick={create}>{he?'לוח חדש':'New dashboard'}</button>
        <button type="button" className="btn" disabled={busy} onClick={async()=>{const title=await promptAction(he?'שם הלוח':'Dashboard title',definition.title,{lang});if(title?.trim())mutate([{op:'rename',title:title.trim()}]);}}>{he?'שינוי שם הלוח':'Rename dashboard'}</button>
        <button type="button" className="btn" disabled={busy} onClick={remove}>{he?'מחיקת הלוח':'Delete dashboard'}</button>
        {!report&&<button type="button" className="btn" onClick={()=>openTab('builder')}>{builderWord}</button>}
      </div></details>
      {!report&&<details className="data-dashboard-dates"><summary>{he?'טווח תאריכים':'Date range'} · {supported}/{definition.panels.length}</summary>
      <form className="data-dashboard-dates" onSubmit={e=>{e.preventDefault();try{mutate([{op:'set_range',range:inclusiveRange(start,end)}]);}catch{setError(he?'בחרו טווח תאריכים תקין.':'Choose a valid date range.');}}}>
        <fieldset disabled={busy||supported===0}><legend>{he?'טווח תאריכים':'Date range'}</legend>
          <label>{he?'מתאריך':'From'}<input className="input" type="date" required value={start} max="9999-12-30" onChange={e=>setStart(e.target.value)}/></label>
          <label>{he?'עד תאריך (כולל)':'Through (inclusive)'}<input className="input" type="date" required value={end} max="9999-12-30" onChange={e=>setEnd(e.target.value)}/></label>
          <button type="submit" className="btn">{he?'החלת טווח':'Apply range'}</button>
          <button type="button" className="btn" disabled={!definition.range} onClick={()=>mutate([{op:'set_range',range:null}])}>{he?'טווחים מקוריים':'Original ranges'}</button>
        </fieldset>
        <p className="muted">{he?`${supported} מתוך ${definition.panels.length} פריטים תומכים בטווח התאריכים. ללא בחירה, כל פריט משתמש בטווח המקורי.`:`${supported} of ${definition.panels.length} panels support the date range. Without a selection, each uses its original range.`}</p>
      </form></details>}</div>
      {report?<div className="bi-workspace" role="tabpanel" id={`${uid}-panel`} aria-labelledby={`${uid}-tab`} data-pane={paneOpen?'open':'closed'}>
        <div className="bi-canvas">{grid}</div>
        <FilterPane id={uid} open={paneOpen} onToggle={()=>setPaneOpen(o=>!o)} params={params} dirty={!!state.draft} disabled={busy} today={today}
          years={years} dateTitles={dateTitles} choices={choices} onChange={changeParams} onReset={()=>changeParams(normalParams(definition.params))}
          onSave={()=>mutate(saveOperations(current.current))} lang={lang}/>
      </div>:grid}
    </>:!busy&&<><div className="data-dashboard-options"><button type="button" className="btn" onClick={create}>{he?'לוח חדש':'New dashboard'}</button><button type="button" className="btn" data-open-builder onClick={()=>openTab('builder')}>{builderWord}</button></div><p className="data-dashboard-empty">{he?'בחרו לוח או צרו אחד. שמרו תוצאות מהשיחה כדי לראות נתונים עדכניים במקום אחד.':'Choose or create a dashboard. Save results from Talk to see current data together.'}</p></>}
    {(tabs||built)&&<div className="bi-builder-tab" role="tabpanel" id={`${uid}-builder`} aria-labelledby={`${uid}-builder-tab`} hidden={!building}>
      {built&&<ReportBuilder key={`${scopeKey}|${env}`} id={`${uid}-build`} env={env} lang={lang} board={definition} boards={boards} busy={busy} onSave={saveReport}/>}
    </div>}
  </section>;
}
